import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  ExpandMore,
  CheckCircle,
  Warning,
  Info,
  Timeline,
  Gavel,
  Home,
  AttachMoney,
  Description,
  Search
} from '@mui/icons-material';

const ForeclosureGuide = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  const foreclosureSteps = [
    {
      label: 'Pre-Foreclosure Notice',
      description: 'Property owner receives notice of default from lender',
      timeframe: '30-120 days',
      details: 'The foreclosure process begins when a homeowner misses mortgage payments. The lender sends a Notice of Default or Lis Pendens, starting the legal foreclosure process.',
      opportunities: 'Limited opportunities; property not yet available for sale',
      risks: 'None for investors at this stage'
    },
    {
      label: 'Foreclosure Filing',
      description: 'Lender files foreclosure lawsuit with the court',
      timeframe: '60-90 days',
      details: 'The lender files a foreclosure complaint in court. The property owner has time to respond or work out a solution with the lender.',
      opportunities: 'Short sale opportunities may arise',
      risks: 'Legal complications possible'
    },
    {
      label: 'Auction Notice',
      description: 'Court orders property to be sold at sheriff\'s auction',
      timeframe: '30-45 days',
      details: 'The court issues a judgment and sets an auction date. Public notice is given, and the property is advertised.',
      opportunities: 'Preparation time for auction research',
      risks: 'High competition expected'
    },
    {
      label: 'Sheriff\'s Auction',
      description: 'Property is sold to highest bidder at public auction',
      timeframe: 'Auction day',
      details: 'The property is sold at public auction to the highest bidder. Payment terms vary by county but usually require immediate down payment.',
      opportunities: 'Primary investment opportunity',
      risks: 'No inspection period, as-is condition'
    },
    {
      label: 'REO (Real Estate Owned)',
      description: 'If no bidders, property becomes bank-owned',
      timeframe: 'Post-auction',
      details: 'If the property doesn\'t sell at auction, it becomes REO (Real Estate Owned) by the lender and may be listed for sale.',
      opportunities: 'Negotiable terms, inspection possible',
      risks: 'Higher prices than auction'
    }
  ];

  const keyTerms = [
    {
      term: 'Upset Value',
      definition: 'The minimum bid amount set by the court, typically the loan balance plus costs'
    },
    {
      term: 'Sheriff\'s Deed',
      definition: 'Legal document transferring property ownership to the auction winner'
    },
    {
      term: 'Redemption Period',
      definition: 'Time after auction when original owner can reclaim property by paying full amount'
    },
    {
      term: 'Lis Pendens',
      definition: 'Legal notice that a property is subject to foreclosure proceedings'
    },
    {
      term: 'REO',
      definition: 'Real Estate Owned - property owned by lender after failed auction'
    },
    {
      term: 'Deficiency Judgment',
      definition: 'Court order for borrower to pay remaining debt after foreclosure sale'
    }
  ];

  const investmentChecklist = [
    'Research property title and liens',
    'Verify upset value and market value',
    'Inspect property exterior (interior usually not accessible)',
    'Check neighborhood comps and trends',
    'Calculate renovation costs and total investment',
    'Secure financing or cash for purchase',
    'Understand auction rules and procedures',
    'Prepare required documentation',
    'Have backup properties identified',
    'Plan post-purchase strategy'
  ];

  const riskFactors = [
    {
      risk: 'Hidden Liens',
      description: 'Property may have undisclosed liens or judgments',
      mitigation: 'Conduct thorough title search before bidding'
    },
    {
      risk: 'Property Condition',
      description: 'Unable to inspect interior; may need major repairs',
      mitigation: 'Budget for worst-case renovation scenarios'
    },
    {
      risk: 'Redemption Rights',
      description: 'Original owner may reclaim property within redemption period',
      mitigation: 'Understand local redemption laws and timeframes'
    },
    {
      risk: 'Occupancy Issues',
      description: 'Property may be occupied by tenants or former owners',
      mitigation: 'Plan for potential eviction costs and delays'
    },
    {
      risk: 'Market Changes',
      description: 'Property values may decline after purchase',
      mitigation: 'Buy with sufficient margin of safety'
    }
  ];

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 700, 
            color: '#1B5E20',
            mb: 2 
          }}>
            Complete Foreclosure Guide
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#666',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6
          }}>
            Master the foreclosure investment process with our comprehensive guide. 
            Learn every step from filing to auction and beyond.
          </Typography>
        </Box>

        {/* Overview Alert */}
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Important Notice: Foreclosure laws vary by state. This guide focuses on New Jersey procedures. 
            Always consult with local attorneys and real estate professionals.
          </Typography>
        </Alert>

        {/* Foreclosure Process Timeline */}
        <Paper sx={{ p: 4, mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 4, color: '#1B5E20' }}>
            Foreclosure Process Timeline
          </Typography>
          <Stepper activeStep={activeStep} orientation="vertical">
            {foreclosureSteps.map((step, index) => (
              <Step key={index}>
                <StepLabel 
                  onClick={() => setActiveStep(index)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {step.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {step.description}
                    </Typography>
                    <Chip 
                      label={step.timeframe} 
                      size="small" 
                      color="primary" 
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </StepLabel>
                <StepContent>
                  <Card sx={{ mt: 2, bgcolor: '#f8f9fa' }}>
                    <CardContent>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {step.details}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                            Investment Opportunities:
                          </Typography>
                          <Typography variant="body2">
                            {step.opportunities}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#d32f2f', mb: 1 }}>
                            Risk Factors:
                          </Typography>
                          <Typography variant="body2">
                            {step.risks}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => setActiveStep(index + 1)}
                      sx={{ mr: 1, bgcolor: '#1B5E20' }}
                      disabled={index === foreclosureSteps.length - 1}
                    >
                      Next Step
                    </Button>
                    <Button
                      onClick={() => setActiveStep(index - 1)}
                      disabled={index === 0}
                    >
                      Back
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Key Terms Dictionary */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
            Key Terms & Definitions
          </Typography>
          <Grid container spacing={2}>
            {keyTerms.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1B5E20', mb: 1 }}>
                      {item.term}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {item.definition}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Investment Checklist */}
        <Paper sx={{ p: 4, mb: 6, bgcolor: '#e8f5e8' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
            Pre-Auction Investment Checklist
          </Typography>
          <Grid container spacing={2}>
            {investmentChecklist.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircle sx={{ color: '#2e7d32', mr: 1, fontSize: 20 }} />
                  <Typography variant="body1">{item}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Risk Factors */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
            Understanding the Risks
          </Typography>
          {riskFactors.map((item, index) => (
            <Accordion 
              key={index}
              expanded={expandedAccordion === `risk${index}`}
              onChange={handleAccordionChange(`risk${index}`)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Warning sx={{ color: '#d32f2f', mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {item.risk}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#d32f2f' }}>
                      Risk Description:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {item.description}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2e7d32' }}>
                      Mitigation Strategy:
                    </Typography>
                    <Typography variant="body2">
                      {item.mitigation}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Success Tips */}
        <Paper sx={{ p: 4, mb: 6, bgcolor: '#1B5E20', color: 'white' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
            Keys to Foreclosure Investment Success
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Search sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Research Thoroughly
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Know the property, neighborhood, and market conditions before bidding
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <AttachMoney sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Conservative Bidding
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Factor in all costs and leave room for unexpected expenses
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Timeline sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Long-term View
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Plan your investment strategy and exit plan before purchasing
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Description sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Professional Help
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Work with experienced attorneys, contractors, and real estate professionals
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Call to Action */}
        <Box sx={{ 
          textAlign: 'center', 
          bgcolor: 'white',
          border: '2px solid #1B5E20',
          borderRadius: 3,
          p: 4
        }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#1B5E20' }}>
            Ready to Start Investing?
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, color: '#666' }}>
            Put your knowledge to work with our comprehensive property database
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            sx={{ 
              bgcolor: '#1B5E20',
              fontWeight: 600,
              px: 4,
              mr: 2
            }}
          >
            Browse Properties
          </Button>
          <Button 
            variant="outlined" 
            size="large"
            sx={{ 
              color: '#1B5E20',
              borderColor: '#1B5E20',
              fontWeight: 600,
              px: 4
            }}
          >
            Download Guide PDF
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ForeclosureGuide;














